/**
 * 에러 메시지 컴포넌트
 *
 * API 오류, 데이터 불러오기 실패 등의 에러 상태를 사용자 친화적으로 표시합니다.
 * 에러 아이콘, 제목, 메시지, 선택적 재시도 버튼을 포함합니다.
 *
 * @component
 * @example
 * // 기본 에러 메시지
 * <AppErrorMessage
 *   title="데이터 로드 실패"
 *   message="플레이리스트 데이터를 불러올 수 없습니다."
 * />
 *
 * @example
 * // 재시도 기능이 있는 에러
 * <AppErrorMessage
 *   title="연결 오류"
 *   message="네트워크 연결을 확인해주세요."
 *   onRetry={() => refetch()}
 * />
 *
 * @typedef {object} AppErrorMessageProps
 * @property {string} [title='Error'] - 에러 제목
 * @property {string} message - 에러 상세 메시지
 * @property {() => void} [onRetry] - 재시도 버튼 클릭 시 호출되는 콜백 (생략 시 버튼 미표시)
 *
 * @returns {JSX.Element} 스타일이 적용된 에러 메시지 카드
 */

'use client';

import { XCircle, RefreshCw } from 'lucide-react';
import { AppButton } from './AppButton';
import { AppCard, AppCardContent } from './AppCard';

export interface AppErrorMessageProps {
  title?: string;
  message: string;
  onRetry?: () => void;
  'data-testid'?: string;
}

export const AppErrorMessage = ({ title = 'Error', message, onRetry, 'data-testid': dataTestId }: AppErrorMessageProps) => {
  return (
    <AppCard className="bg-red-50 border-red-200" data-testid={dataTestId}>
      <AppCardContent className="flex flex-col items-center justify-center gap-4">
        <XCircle className="w-10 h-10 text-red-500" />
        <div className="text-center">
          <h3 className="heading-4 text-red-900 mb-1">{title}</h3>
          <p className="body-base text-red-700">{message}</p>
        </div>
        {onRetry && (
          <AppButton
            onClick={onRetry}
            variant="outline"
            size="sm"
            className="mt-2"
            data-testid="retry-btn"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Retry
          </AppButton>
        )}
      </AppCardContent>
    </AppCard>
  );
};
